import type {CSSResultArray} from 'lit';
import {consume} from '@lit-labs/context';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {SessionContextData} from '#lit/contexts/session.js';
import {SessionContext} from '#lit/contexts/session.js';
import type {Constructor} from '#lit/types/classes.js';
import type {FeatureFlagKey} from '#lit/lib/feature_flags/FeatureFlag.js';
import {featureFlags} from '#lit/lib/feature_flags/FeatureFlag.js';
import type {Model, Msg} from './architecture.js';
import {main} from './architecture.js';

import styles from './style.scss';

export const FEATURE_FLAG_BOUNDARY_ELEMENT_TAG = 'fly-flag-boundary';
const flags = featureFlags;

/**
 * This component gives a declarative way
 * of consuming Feature Flags on templates.
 */
@customElement(FEATURE_FLAG_BOUNDARY_ELEMENT_TAG)
export default class FlyFeatureFlagBoundary extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles(): CSSResultArray {
    return [styles];
  }

  /**
   * The feature flag we want to compute.
   * Feature flags should be provided in a parent session provider.
   */
  @property({
    type: String,
    converter(value) {
      return flags.find(ff => ff.key === value)?.key ?? '';
    },
  })
  key = '' as FeatureFlagKey;

  /**
   * Indicate the children should render when the FF is off
   *
   * Notice that we also have an off attribute for legibility purpose.
   * If off is set up, it will be enforced on top of on.
   *
   * If someone (incorrectly) declares <fly-flag-boundary key="ff" on off>
   * the component will apply the off logic
   */
  @property({type: Boolean})
  on = false;

  /**
   * Indicate the children should render when the FF is off
   *
   * This attribute is for HTML legibility
   * (otherwise we would have an implied on=false)
   *
   * If off is set up, it will be enforced on top of on.
   * If someone (incorrectly) declares <fly-flag-boundary key="ff" on off>
   * the component will apply the off logic
   */
  @property({type: Boolean})
  off = false;

  @consume({context: SessionContext, subscribe: true})
  @property({type: Object})
  session?: SessionContextData;
}

declare global {
  interface HTMLElementTagNameMap {
    [FEATURE_FLAG_BOUNDARY_ELEMENT_TAG]: FlyFeatureFlagBoundary;
  }
}
